import React, { useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Can } from '../../contexts/AbilityContext';

import type { ExternalReviewProviderPublic, ExternalReviewProviderType } from '@clareefai/types';
import {
  Award,
  Building,
  CheckCircle,
  ChevronDown,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  FileJson,
  FileSpreadsheet,
  Globe,
  Grid,
  List,
  Loader,
  MessageSquare,
  Phone,
  Rocket,
  Search,
  Star,
  Target,
  TrendingUp,
  X
} from 'lucide-react';
import ReviewDetailsModal from '../../components/reviews/ReviewDetailsModal';
import PageTitle from '../../components/shared/PageTitle';
import { useGetExtractionStatusQuery, useGetReviewsQuery, useGetVendorMeQuery, useToggleProviderVisibilityMutation, useTriggerReviewExtractionMutation } from '../../store/vendorApi';
import { useGetTeamMembersQuery } from '../../store/teamMemberApi';
import { useGetPromotersQuery } from '../../store/promoterApi';

const PROVIDER_CONFIG: Record<ExternalReviewProviderType, { label: string; icon: typeof Globe; color: string; bgColor: string }> = {
  GOOGLE_REVIEWS: { label: 'Google Reviews', icon: Globe, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  G2: { label: 'G2', icon: Award, color: 'text-orange-600', bgColor: 'bg-orange-100' },
  TRUSTPILOT: { label: 'Trustpilot', icon: Star, color: 'text-green-600', bgColor: 'bg-green-100' },
  CAPTERRA: { label: 'Capterra', icon: Target, color: 'text-purple-600', bgColor: 'bg-purple-100' },
};

// Light background + text colour pairing per review status
const statusBadgeClass = (status: string | undefined): string => {
  const s = typeof status === 'string' ? status.toUpperCase() : String(status ?? '');
  switch (s) {
    case 'PUBLISHED':
      return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100';
    case 'FEATURED':
      return 'bg-amber-50 text-amber-700 ring-1 ring-amber-100';
    case 'COMPLETED':
      return 'bg-slate-100 text-slate-600 ring-1 ring-slate-200';
    default:
      return 'bg-rose-50 text-rose-700 ring-1 ring-rose-100';
  }
};

// Reusable styled dropdown. `active` (a non-default value is selected) gets an
// indigo highlight so it's obvious at a glance which filters are applied.
const FilterSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  width?: string;
  active?: boolean;
  children: React.ReactNode;
}> = ({ value, onChange, ariaLabel, width = 'w-44', active = false, children }) => (
  <div className={`relative shrink-0 ${width}`}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      className={`appearance-none w-full h-10 pl-3.5 pr-9 rounded-xl border text-sm transition-colors cursor-pointer truncate focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
        active
          ? 'bg-indigo-50 border-indigo-300 text-indigo-700 font-medium'
          : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'
      }`}
    >
      {children}
    </select>
    <ChevronDown className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 ${active ? 'text-indigo-500' : 'text-gray-400'}`} />
  </div>
);

// Renders a 0-5 star row. The threshold star (the Nth, for an "N+" filter) shows
// the number inside it in bold dark-yellow; the rest are full or empty outlines.
const RatingStars: React.FC<{ value: number; size?: number }> = ({ value, size = 16 }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => {
      const filled = n <= value;
      return (
        <span key={n} className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
          <Star className={filled ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'} style={{ width: size, height: size }} />
          {n === value && (
            <span className="absolute font-bold text-yellow-800 leading-none" style={{ fontSize: size * 0.55 }}>{value}</span>
          )}
        </span>
      );
    })}
  </span>
);

// Lightweight popover that closes on outside click via a transparent overlay
// (no effect / click-outside listener needed).
const Popover: React.FC<{
  open: boolean;
  onClose: () => void;
  align?: 'left' | 'right';
  children: React.ReactNode;
}> = ({ open, onClose, align = 'left', children }) =>
  !open ? null : (
    <>
      <div className="fixed inset-0 z-10" onClick={onClose} />
      <div className={`absolute z-20 mt-1 ${align === 'right' ? 'right-0' : 'left-0'} min-w-[12rem] bg-white border border-gray-200 rounded-xl shadow-lg p-1.5`}>
        {children}
      </div>
    </>
  );

// Star-based rating filter dropdown.
const RatingFilter: React.FC<{
  value: 'all' | '5' | '4' | '3';
  onChange: (value: 'all' | '5' | '4' | '3') => void;
  anyLabel: string;
  ariaLabel: string;
}> = ({ value, onChange, anyLabel, ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const active = value !== 'all';
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={ariaLabel}
        className={`flex items-center gap-2 h-10 pl-3.5 pr-3 rounded-xl border text-sm transition-colors ${
          active
            ? 'bg-indigo-50 border-indigo-300 text-indigo-700 font-medium'
            : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'
        }`}
      >
        {active ? <RatingStars value={Number(value)} /> : <span>{anyLabel}</span>}
        <ChevronDown className={`w-4 h-4 ${active ? 'text-indigo-500' : 'text-gray-400'}`} />
      </button>
      <Popover open={open} onClose={() => setOpen(false)}>
        <button
          type="button"
          onClick={() => { onChange('all'); setOpen(false); }}
          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${value === 'all' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
        >
          {anyLabel}
        </button>
        {[5, 4, 3].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => { onChange(String(n) as '5' | '4' | '3'); setOpen(false); }}
            className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${value === String(n) ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
          >
            <RatingStars value={n} size={18} />
          </button>
        ))}
      </Popover>
    </div>
  );
};

// Export dropdown: click reveals CSV / JSON options.
const ExportMenu: React.FC<{
  onCsv: () => void;
  onJson: () => void;
  disabled?: boolean;
  label: string;
  csvLabel: string;
  jsonLabel: string;
}> = ({ onCsv, onJson, disabled = false, label, csvLabel, jsonLabel }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 h-10 px-4 text-sm font-medium text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Download className="w-4 h-4" />
        {label}
        <ChevronDown className="w-4 h-4" />
      </button>
      <Popover open={open} onClose={() => setOpen(false)} align="right">
        <button
          type="button"
          onClick={() => { onCsv(); setOpen(false); }}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
          {csvLabel}
        </button>
        <button
          type="button"
          onClick={() => { onJson(); setOpen(false); }}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <FileJson className="w-4 h-4 text-amber-600" />
          {jsonLabel}
        </button>
      </Popover>
    </div>
  );
};

// Shared-element view-transition names morphed between card and detail page.
const VT = { title: 'review-title', company: 'review-company', rating: 'review-rating' } as const;

// A single grid card. When clicked, it tags its title/company/rating with
// view-transition-name and drives a browser View Transition so those elements
// morph into their counterparts on the detail page. Uses the manual
// startViewTransition + flushSync pattern because the app uses <BrowserRouter>
// (React Router's built-in viewTransition only works with a data RouterProvider).
const ReviewGridCard: React.FC<{
  review: any;
  returnId?: string;
  formatStatus: (status: string | undefined) => string;
}> = ({ review, returnId, formatStatus }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const to = `/reviews/${review.id}`;
  const titleRef = useRef<HTMLHeadingElement>(null);
  const companyRef = useRef<HTMLParagraphElement>(null);
  const ratingRef = useRef<HTMLDivElement>(null);

  // When we've just returned from this review's detail page, tag this card's
  // elements so the detail header morphs back into the card (reverse direction).
  const isReturnTarget = returnId === review.id;
  const sharedStyle = (name: string): React.CSSProperties | undefined =>
    isReturnTarget ? { viewTransitionName: name } : undefined;

  const open = () => {
    const startViewTransition = (document as any).startViewTransition?.bind(document);
    if (!startViewTransition) {
      navigate(to, { state: { review } });
      return;
    }
    // Clear any names left over from a prior reverse transition so each name
    // stays unique, then tag only this card's elements as the morph source.
    document.querySelectorAll<HTMLElement>('[data-vt-shared]').forEach((el) => el.style.removeProperty('view-transition-name'));
    titleRef.current?.style.setProperty('view-transition-name', VT.title);
    companyRef.current?.style.setProperty('view-transition-name', VT.company);
    ratingRef.current?.style.setProperty('view-transition-name', VT.rating);
    startViewTransition(() => {
      flushSync(() => navigate(to, { state: { review } }));
    });
  };

  return (
    <div
      className="group flex h-full flex-col border border-gray-200 rounded-2xl p-4 bg-white cursor-pointer transition-[box-shadow,transform] duration-200 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98]"
      style={{ transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)' }}
      onClick={open}
    >
      {/* Promoter identity */}
      <div className="flex items-center space-x-3 mb-3">
        <div className="w-10 h-10 flex-shrink-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
          {review.promoterName.split(' ').map((n: string) => n[0]).join('')}
        </div>
        <div className="flex-1 min-w-0">
          <h4 ref={titleRef} data-vt-shared={isReturnTarget ? '' : undefined} style={sharedStyle(VT.title)} className="font-semibold text-gray-900 text-sm truncate">{review.promoterName}</h4>
          <p ref={companyRef} data-vt-shared={isReturnTarget ? '' : undefined} style={sharedStyle(VT.company)} className="text-xs text-gray-500 truncate">{review.promoterCompany || t('reviews.list.noCompany')}</p>
        </div>
      </div>

      {/* Status badges — own line. Fixed height so cards with/without the
          featured badge keep the rows below them vertically aligned. */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3 min-h-[1.75rem]">
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusBadgeClass(review.status)}`}>
          {formatStatus(review.status)}
        </span>
        {(review.isFeatured || (typeof review.status === 'string' && review.status.toUpperCase() === 'FEATURED')) && (
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-100">
            {t('reviews.list.featuredBadge')}
          </span>
        )}
      </div>

      {/* Rating — own line */}
      <div ref={ratingRef} data-vt-shared={isReturnTarget ? '' : undefined} style={sharedStyle(VT.rating)} className="mb-2 w-fit">
        <RatingStars value={review.rating || 0} />
      </div>

      {/* Content — own line. Reserve 3 lines so short reviews don't collapse
          the card and the meta block below stays aligned across the grid. */}
      <p className="text-xs text-gray-700 line-clamp-3 leading-relaxed mb-3 min-h-[3.75rem] [text-wrap:pretty]">{review.content || t('reviews.list.noContent')}</p>

      <div className="space-y-2 mb-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">{t('reviews.list.product')}</span>
          <span className="font-medium text-gray-700 truncate ml-1">{review.productId || t('reviews.list.naShort')}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">{t('reviews.list.date')}</span>
          <span className="font-medium text-gray-700 tabular-nums">{new Date(review.createdAt).toLocaleDateString('en-US')}</span>
        </div>
      </div>

      {review.rejectionReason && (
        <div className="bg-red-50 border-l-4 border-red-400 p-2 rounded-lg mb-3">
          <div className="flex items-center space-x-1 mb-1">
            <span className="text-xs font-medium text-red-900">{t('reviews.list.rejectionReason')}</span>
          </div>
          <p className="text-xs text-red-800 line-clamp-2">{review.rejectionReason}</p>
        </div>
      )}

      {review.vendorResponse && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-lg mb-3">
          <div className="flex items-center space-x-2 mb-1">
            <Building className="w-3 h-3 text-blue-600" />
            <span className="text-xs font-medium text-blue-900">{t('reviews.list.response')}</span>
          </div>
          <p className="text-xs text-blue-800 line-clamp-2">{review.vendorResponse}</p>
        </div>
      )}

      {/* Footer pinned to the bottom of the (equal-height) card via mt-auto, so
          the View button lines up across every card regardless of content. */}
      <div className="mt-auto flex items-center space-x-2 pt-1">
        <button
          onClick={(e) => { e.stopPropagation(); open(); }}
          className="flex-1 text-blue-600 hover:text-blue-800 text-xs font-medium py-1.5 px-2 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors active:scale-[0.96]"
        >
          {t('common.view')}
        </button>
      </div>
    </div>
  );
};

const ReviewsManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  // Set when returning from a review's detail page — the matching card morphs back.
  const returnId = (location.state as { vtReturnId?: string } | null)?.vtReturnId;

  const formatStatus = (status: string | undefined): string => {
    if (!status) return '';
    const statusUpper = typeof status === 'string' ? status.toUpperCase() : String(status).toUpperCase();
    switch (statusUpper) {
      case 'COMPLETED':
        return t('reviews.status.completed');
      case 'PUBLISHED':
        return t('reviews.status.published');
      case 'FEATURED':
        return t('reviews.status.featured');
      default:
        return status;
    }
  };
  const [activeTab, setActiveTab] = useState<'reviews' | 'external' | 'widget'>('reviews');
  const [reviewFilter, setReviewFilter] = useState<'all' | 'featured' | 'verified' | 'completed' | 'published'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [repFilter, setRepFilter] = useState('');
  const [promoterFilter, setPromoterFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState<'all' | '5' | '4' | '3'>('all');
  const [datePreset, setDatePreset] = useState<'all' | '7d' | '30d' | '90d' | 'custom'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showReviewSelectionModal, setShowReviewSelectionModal] = useState(false);
  const [selectedReviewsForWidget, setSelectedReviewsForWidget] = useState<string[]>([]);
  const [selectedReview, setSelectedReview] = useState<any>(null);
  const [showReviewDetails, setShowReviewDetails] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Fetch reviews by status
  const { data: completedReviews = [], isLoading: loadingCompleted } = useGetReviewsQuery({ status: 'COMPLETED' });
  const { data: publishedReviews = [], isLoading: loadingPublished } = useGetReviewsQuery({ status: 'PUBLISHED' });
  const { data: featuredReviewsList = [], isLoading: loadingFeatured } = useGetReviewsQuery({ status: 'FEATURED' });

  // Combine and dedupe by id (prefer FEATURED when duplicate)
  const allReviews = React.useMemo(() => {
    const combined = [...completedReviews, ...publishedReviews, ...featuredReviewsList];
    const byId = new Map<string, (typeof combined)[0]>();
    combined.forEach((r) => {
      const existing = byId.get(r.id);
      const statusUpper = typeof r.status === 'string' ? r.status.toUpperCase() : (r.status as string) ?? '';
      const existingUpper = existing ? (typeof existing.status === 'string' ? existing.status.toUpperCase() : (existing.status as string) ?? '') : '';
      if (!existing || (statusUpper === 'FEATURED' && existingUpper !== 'FEATURED')) {
        byId.set(r.id, r);
      }
    });
    return Array.from(byId.values());
  }, [completedReviews, publishedReviews, featuredReviewsList]);
  const isLoading = loadingCompleted || loadingPublished || loadingFeatured;


  const { data: vendor } = useGetVendorMeQuery();
  const { data: teamMembersData = [] } = useGetTeamMembersQuery();
  const { data: promotersData = [] } = useGetPromotersQuery();
  const [triggerExtraction, { isLoading: isExtracting }] = useTriggerReviewExtractionMutation();
  const { data: extractionJobs } = useGetExtractionStatusQuery(undefined, { pollingInterval: 10_000 });
  const [toggleVisibility] = useToggleProviderVisibilityMutation();

  const externalProviders: ExternalReviewProviderPublic[] = vendor?.pageInfo?.externalReviewProviders ?? [];

  const latestJobByProvider = React.useMemo(() => {
    if (!extractionJobs) return {} as Record<string, (typeof extractionJobs)[number]>;
    const map: Record<string, (typeof extractionJobs)[number]> = {};
    for (const job of extractionJobs) {
      if (!map[job.provider] || new Date(job.createdAt) > new Date(map[job.provider].createdAt)) {
        map[job.provider] = job;
      }
    }
    return map;
  }, [extractionJobs]);

  const handleExtractAll = async () => {
    try { await triggerExtraction().unwrap(); } catch { /* handled by RTK */ }
  };

  const handleToggleVisibility = async (provider: ExternalReviewProviderType, currentVisible: boolean) => {
    try { await toggleVisibility({ provider, visible: !currentVisible }).unwrap(); } catch { /* handled by RTK */ }
  };

  const promoterIdToAssignedRepId = Object.fromEntries(
    promotersData.map(p => [p.id, p.assignedTeamMember?.id])
  );

  // Resolve the active date range (inclusive) from the preset / custom inputs
  const dateRange = React.useMemo(() => {
    if (datePreset === 'custom') {
      const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
      const to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;
      return { from, to };
    }
    const days = datePreset === '7d' ? 7 : datePreset === '30d' ? 30 : datePreset === '90d' ? 90 : null;
    if (days === null) return { from: null, to: null };
    const from = new Date();
    from.setDate(from.getDate() - days);
    from.setHours(0, 0, 0, 0);
    return { from, to: null };
  }, [datePreset, dateFrom, dateTo]);

  const filteredReviews = allReviews.filter(review => {
    const statusUpper = typeof review.status === 'string' ? review.status.toUpperCase() : (review.status as string) ?? '';
    const matchesFilter = reviewFilter === 'all' ||
      (reviewFilter === 'featured' && (review.isFeatured === true || statusUpper === 'FEATURED')) ||
      (reviewFilter === 'completed' && statusUpper === 'COMPLETED') ||
      (reviewFilter === 'published' && statusUpper === 'PUBLISHED');

    if (repFilter && promoterIdToAssignedRepId[review.promoterId] !== repFilter) return false;

    if (promoterFilter && review.promoterId !== promoterFilter) return false;

    if (ratingFilter !== 'all' && (review.rating ?? 0) < Number(ratingFilter)) return false;

    if (dateRange.from || dateRange.to) {
      const created = new Date(review.createdAt);
      if (dateRange.from && created < dateRange.from) return false;
      if (dateRange.to && created > dateRange.to) return false;
    }

    const matchesSearch = searchTerm === '' ||
      review.promoterName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (review.promoterCompany && review.promoterCompany.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (review.content && review.content.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesFilter && matchesSearch;
  });

  const reviewStats = {
    total: allReviews.length,
    featured: allReviews.filter(r => r.status === 'FEATURED').length,
    published: allReviews.filter(r => r.status === 'PUBLISHED').length,
    completed: allReviews.filter(r => r.status === 'COMPLETED').length,
    averageRating: allReviews.length > 0
      ? (allReviews.reduce((sum, r) => sum + (r.rating || 0), 0) / allReviews.length).toFixed(1)
      : '0.0'
  };

  const handleReviewToggleForWidget = (reviewId: string) => {
    setSelectedReviewsForWidget(prev =>
      prev.includes(reviewId)
        ? prev.filter(id => id !== reviewId)
        : [...prev, reviewId]
    );
  };

  const getSelectedReviews = () => {
    return allReviews.filter(review => selectedReviewsForWidget.includes(review.id));
  };

  const handleReviewClick = (review: any) => {
    navigate(`/reviews/${review.id}`);
  };

  const handleUpdateReview = (_updatedReview: any) => {
    setShowReviewDetails(false);
  };

  const escapeCsvField = (value: string | number | boolean | null | undefined) => {
    const str = String(value ?? '');
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const triggerDownload = (blob: Blob, ext: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reviews-export-${new Date().toISOString().slice(0, 10)}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadReviewsCsv = () => {
    const reviews = filteredReviews;
    const headers = [
      t('reviews.csv.name'),
      t('reviews.csv.company'),
      t('reviews.csv.rating'),
      t('reviews.csv.status'),
      t('reviews.csv.featured'),
      t('reviews.csv.product'),
      t('reviews.csv.content'),
      t('reviews.csv.vendorResponse'),
      t('reviews.csv.submittedDate'),
    ];
    const rows = reviews.map((r) => [
      escapeCsvField(r.promoterName),
      escapeCsvField(r.promoterCompany),
      escapeCsvField(r.rating),
      escapeCsvField(r.status),
      escapeCsvField(r.isFeatured ? t('reviews.csv.yes') : t('reviews.csv.no')),
      escapeCsvField(r.productId),
      escapeCsvField(r.content),
      escapeCsvField(r.vendorResponse),
      escapeCsvField(r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-US') : ''),
    ]);
    const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    triggerDownload(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), 'csv');
  };

  const downloadReviewsJson = () => {
    const data = filteredReviews.map((r) => ({
      name: r.promoterName,
      company: r.promoterCompany ?? null,
      rating: r.rating ?? null,
      status: r.status,
      featured: !!r.isFeatured,
      product: r.productId ?? null,
      content: r.content ?? null,
      vendorResponse: r.vendorResponse ?? null,
      submittedDate: r.createdAt ?? null,
    }));
    triggerDownload(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }), 'json');
  };

  const hasActiveFilters =
    searchTerm !== '' || reviewFilter !== 'all' || repFilter !== '' ||
    promoterFilter !== '' || ratingFilter !== 'all' || datePreset !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setReviewFilter('all');
    setRepFilter('');
    setPromoterFilter('');
    setRatingFilter('all');
    setDatePreset('all');
    setDateFrom('');
    setDateTo('');
  };

  // Compact inline stats shown next to the page title
  const renderInlineStats = () => (
    <div className="flex items-center gap-5 sm:gap-6">
      <div className="text-center">
        <div className="text-xl font-bold text-blue-600 leading-tight">{reviewStats.total}</div>
        <div className="text-xs text-gray-500">{t('reviews.stats.total')}</div>
      </div>
      <div className="text-center">
        <div className="text-xl font-bold text-yellow-600 leading-tight">{reviewStats.averageRating}</div>
        <div className="text-xs text-gray-500">{t('reviews.stats.averageRating')}</div>
      </div>
      <div className="text-center">
        <div className="text-xl font-bold text-gray-600 leading-tight">{reviewStats.completed}</div>
        <div className="text-xs text-gray-500">{t('reviews.stats.completed')}</div>
      </div>
      <div className="text-center">
        <div className="text-xl font-bold text-green-600 leading-tight">{reviewStats.published}</div>
        <div className="text-xs text-gray-500">{t('reviews.stats.published')}</div>
      </div>
      <div className="text-center">
        <div className="text-xl font-bold text-purple-600 leading-tight">{reviewStats.featured}</div>
        <div className="text-xs text-gray-500">{t('reviews.stats.featured')}</div>
      </div>
    </div>
  );

  const renderReviews = () => (
    <div className="space-y-6">
      {/* Filters and search */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="space-y-3 mb-6">
          {/* Row 1: search + view toggle + export */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('reviews.list.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-10 pl-10 pr-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 placeholder:text-gray-400 hover:bg-gray-100 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent focus:bg-white transition-colors"
              />
            </div>

            <div className="flex items-center gap-1 h-10 border border-gray-200 bg-gray-50 rounded-xl p-1 shrink-0">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-lg transition-colors ${viewMode === 'grid'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-gray-500 hover:bg-white hover:text-gray-700'
                  }`}
                title={t('reviews.list.gridViewTitle')}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-lg transition-colors ${viewMode === 'list'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-gray-500 hover:bg-white hover:text-gray-700'
                  }`}
                title={t('reviews.list.listViewTitle')}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            <ExportMenu
              onCsv={downloadReviewsCsv}
              onJson={downloadReviewsJson}
              disabled={filteredReviews.length === 0}
              label={t('reviews.list.export')}
              csvLabel={t('reviews.list.exportAsCsv')}
              jsonLabel={t('reviews.list.exportAsJson')}
            />
          </div>

          {/* Row 2: filters on a single line (wraps only on narrow screens) */}
          <div className="flex flex-wrap items-center gap-3">
            <FilterSelect value={reviewFilter} onChange={(v) => setReviewFilter(v as any)} ariaLabel={t('reviews.list.filterAll')} active={reviewFilter !== 'all'} width="w-40">
              <option value="all">{t('reviews.list.filterAll')}</option>
              <option value="completed">{t('reviews.list.filterCompleted')}</option>
              <option value="published">{t('reviews.list.filterPublished')}</option>
              <option value="featured">{t('reviews.list.filterFeatured')}</option>
            </FilterSelect>

            <FilterSelect value={repFilter} onChange={setRepFilter} ariaLabel={t('reviews.list.allReps')} active={repFilter !== ''} width="w-40">
              <option value="">{t('reviews.list.allReps', 'All reps')}</option>
              {teamMembersData.map(m => (
                <option key={m.id} value={m.id}>{m.firstName} {m.lastName}</option>
              ))}
            </FilterSelect>

            <FilterSelect value={promoterFilter} onChange={setPromoterFilter} ariaLabel={t('reviews.list.allPromoters')} active={promoterFilter !== ''} width="w-52">
              <option value="">{t('reviews.list.allPromoters')}</option>
              {promotersData.map(p => (
                <option key={p.id} value={p.id}>{p.firstName} {p.lastName}{p.company ? ` — ${p.company}` : ''}</option>
              ))}
            </FilterSelect>

            <RatingFilter
              value={ratingFilter}
              onChange={setRatingFilter}
              anyLabel={t('reviews.list.allRatings')}
              ariaLabel={t('reviews.list.allRatings')}
            />

            <FilterSelect value={datePreset} onChange={(v) => setDatePreset(v as any)} ariaLabel={t('reviews.list.dateAll')} active={datePreset !== 'all'} width="w-40">
              <option value="all">{t('reviews.list.dateAll')}</option>
              <option value="7d">{t('reviews.list.date7')}</option>
              <option value="30d">{t('reviews.list.date30')}</option>
              <option value="90d">{t('reviews.list.date90')}</option>
              <option value="custom">{t('reviews.list.dateCustom')}</option>
            </FilterSelect>

            {datePreset === 'custom' && (
              <div className="flex items-center gap-2 shrink-0">
                <input
                  type="date"
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(e) => setDateFrom(e.target.value)}
                  aria-label={t('reviews.list.dateFrom')}
                  className="h-10 px-3 bg-indigo-50 border border-indigo-300 rounded-xl text-sm text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                />
                <span className="text-gray-400 text-sm">–</span>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => setDateTo(e.target.value)}
                  aria-label={t('reviews.list.dateTo')}
                  className="h-10 px-3 bg-indigo-50 border border-indigo-300 rounded-xl text-sm text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                />
              </div>
            )}

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 h-10 px-3 shrink-0 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                title={t('reviews.list.clearFilters')}
              >
                <X className="w-4 h-4" />
                {t('reviews.list.clearFilters')}
              </button>
            )}
          </div>
        </div>

        {/* Filtered result count — only meaningful when a filter/search is active */}
        {hasActiveFilters && !isLoading && (
          <p className="text-sm text-gray-500 mb-4">
            {t('reviews.list.resultsCount', { shown: filteredReviews.length, total: allReviews.length })}
          </p>
        )}

        {/* Reviews display */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-600">{t('reviews.list.loading')}</span>
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="text-center py-12">
            <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">{t('reviews.list.empty')}</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-fr items-stretch">
            {filteredReviews.map((review) => (
              <ReviewGridCard key={review.id} review={review} returnId={returnId} formatStatus={formatStatus} />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reviews.table.author')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reviews.table.rating')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reviews.table.comment')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reviews.table.product')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reviews.table.sector')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reviews.table.date')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reviews.table.status')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reviews.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReviews.map((review) => (
                  <tr
                    key={review.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleReviewClick(review)}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {review.promoterName.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{review.promoterName}</div>
                          <div className="text-xs text-gray-500">{review.promoterCompany || t('reviews.list.noCompany')}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <RatingStars value={review.rating || 0} />
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="max-w-md">
                        <p className="text-sm text-gray-700 line-clamp-2">{review.content || t('reviews.list.noContent')}</p>
                        {review.vendorResponse && (
                          <div className="mt-2 bg-blue-50 border-l-4 border-blue-400 p-2 rounded">
                            <div className="flex items-center space-x-1 mb-1">
                              <Building className="w-3 h-3 text-blue-600" />
                              <span className="text-xs font-medium text-blue-900">{t('reviews.list.response')}</span>
                            </div>
                            <p className="text-xs text-blue-800 line-clamp-2">{review.vendorResponse}</p>
                          </div>
                        )}
                        {review.rejectionReason && (
                          <div className="mt-2 bg-red-50 border-l-4 border-red-400 p-2 rounded">
                            <div className="flex items-center space-x-1 mb-1">
                              <span className="text-xs font-medium text-red-900">{t('reviews.list.rejectionReason')}</span>
                            </div>
                            <p className="text-xs text-red-800 line-clamp-1">{review.rejectionReason}</p>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">{review.productId || t('reviews.list.naShort')}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">-</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700">{new Date(review.createdAt).toLocaleDateString('en-US')}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col items-start space-y-1">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${statusBadgeClass(review.status)}`}>
                          {formatStatus(review.status)}
                        </span>
                        {(review.isFeatured || (typeof review.status === 'string' && review.status.toUpperCase() === 'FEATURED')) && (
                          <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-100">
                            {t('reviews.list.featuredBadge')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/reviews/${review.id}`, { state: { review } });
                          }}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium py-1 px-2 border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                        >
                          {t('common.view')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderExternal = () => {
    const completedProviders = externalProviders.filter(p => p.averageRating != null);
    const totalReviews = completedProviders.reduce((sum, p) => sum + (p.totalReviews ?? 0), 0);
    const avgRating = completedProviders.length > 0
      ? (completedProviders.reduce((sum, p) => sum + (p.averageRating ?? 0), 0) / completedProviders.length).toFixed(1)
      : '—';
    const visibleCount = completedProviders.filter(p => p.visible !== false).length;
    const allProviderTypes: ExternalReviewProviderType[] = ['GOOGLE_REVIEWS', 'G2', 'TRUSTPILOT', 'CAPTERRA'];

    return (
      <div className="space-y-6">
        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('reviews.external.platformsFound')}</p>
                <p className="text-3xl font-bold text-blue-600">{completedProviders.length}</p>
                <p className="text-sm text-gray-500">{t('reviews.external.visibleOnPage', { count: visibleCount })}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Globe className="w-8 h-8 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('reviews.external.averageRating')}</p>
                <p className="text-3xl font-bold text-yellow-600">{avgRating}{avgRating !== '—' && '/5'}</p>
                <p className="text-sm text-gray-500">{t('reviews.external.acrossPlatforms')}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Star className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('reviews.external.totalReviews')}</p>
                <p className="text-3xl font-bold text-green-600">{totalReviews.toLocaleString()}</p>
                <p className="text-sm text-gray-500">{t('reviews.external.acrossAllPlatforms')}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <MessageSquare className="w-8 h-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('reviews.external.extractionStatus')}</p>
                <p className="text-3xl font-bold text-purple-600">
                  {Object.values(latestJobByProvider).some(j => j.status === 'IN_PROGRESS' || j.status === 'PENDING') ? (
                    <span className="flex items-center gap-2"><Loader className="w-6 h-6 animate-spin" /> {t('reviews.external.running')}</span>
                  ) : Object.keys(latestJobByProvider).length > 0 ? t('reviews.external.idle') : '—'}
                </p>
                <p className="text-sm text-gray-500">
                  {t('reviews.external.doneCount', {
                    done: Object.values(latestJobByProvider).filter(j => j.status === 'COMPLETED').length,
                    total: Object.keys(latestJobByProvider).length,
                  })}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* External review platforms */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">{t('reviews.external.heading')}</h3>
            <Can I="create" a="Review">
              <button
                onClick={handleExtractAll}
                disabled={isExtracting}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExtracting ? <Loader className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                <span>{isExtracting ? t('reviews.external.extracting') : t('reviews.external.extractAll')}</span>
              </button>
            </Can>
          </div>

          {!vendor?.pageInfo?.companyUrl && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>{t('reviews.external.companyUrlPrefix')}</strong>{' '}
                {t('reviews.external.companyUrlBefore')}{' '}
                <a href="/settings" className="underline hover:text-amber-900">{t('common.settings')}</a>{' '}
                {t('reviews.external.companyUrlAfter')}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {allProviderTypes.map((providerType) => {
              const config = PROVIDER_CONFIG[providerType];
              const providerData = externalProviders.find(p => p.provider === providerType);
              const job = latestJobByProvider[providerType];
              const isVisible = providerData?.visible !== false;
              const isRunning = job?.status === 'IN_PROGRESS' || job?.status === 'PENDING';
              const hasFailed = job?.status === 'FAILED';
              const Icon = config.icon;

              return (
                <div key={providerType} className={`border rounded-lg p-6 transition-shadow hover:shadow-md ${!isVisible && providerData ? 'border-gray-200 bg-gray-50/50' : 'border-gray-200'}`}>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${config.bgColor}`}>
                      <Icon className={`w-6 h-6 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900">{config.label}</h4>
                      {providerData?.averageRating != null && (
                        <div className="flex items-center space-x-2 mt-1">
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-4 h-4 ${i < Math.floor(providerData.averageRating!) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                            ))}
                          </div>
                          <span className="text-sm font-medium text-gray-700">{providerData.averageRating.toFixed(1)}/5</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isRunning && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 flex items-center gap-1">
                          <Loader className="w-3 h-3 animate-spin" />
                          {job?.status === 'PENDING' ? t('reviews.external.queued') : t('reviews.external.extractingStatus')}
                        </span>
                      )}
                      {!isRunning && providerData?.averageRating != null && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {t('reviews.external.activeStatus')}
                        </span>
                      )}
                      {!isRunning && hasFailed && !providerData?.averageRating && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          {t('reviews.external.failedStatus')}
                        </span>
                      )}
                      {!isRunning && !providerData && !hasFailed && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          {t('reviews.external.notExtracted')}
                        </span>
                      )}
                    </div>
                  </div>

                  {providerData?.averageRating != null && (
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-xl font-bold text-gray-900">{(providerData.totalReviews ?? 0).toLocaleString()}</div>
                        <div className="text-sm text-gray-600">{t('reviews.external.totalReviews')}</div>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-xl font-bold text-gray-900">{providerData.averageRating.toFixed(1)}</div>
                        <div className="text-sm text-gray-600">{t('reviews.external.ratingLabel')}</div>
                      </div>
                    </div>
                  )}

                  {hasFailed && job?.errorMessage && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg">
                      <p className="text-sm text-red-700">{job.errorMessage}</p>
                    </div>
                  )}

                  {!providerData && !isRunning && !hasFailed && (
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg text-center">
                      <p className="text-sm text-gray-500">{t('reviews.external.noDataYet', { platform: config.label })}</p>
                    </div>
                  )}

                  <div className="flex items-center space-x-2">
                    {providerData?.profileUrl && (
                      <a
                        href={providerData.profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 text-blue-600 hover:text-blue-800 text-sm font-medium py-2 px-3 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-center flex items-center justify-center space-x-1"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>{t('reviews.external.viewOn', { platform: config.label })}</span>
                      </a>
                    )}
                    {providerData && (
                      <button
                        onClick={() => handleToggleVisibility(providerType, isVisible)}
                        className={`text-sm font-medium py-2 px-3 border rounded-lg transition-colors flex items-center gap-1.5 ${isVisible
                            ? 'text-gray-600 hover:text-gray-800 border-gray-200 hover:bg-gray-50'
                            : 'text-amber-600 hover:text-amber-800 border-amber-200 hover:bg-amber-50'
                          }`}
                        title={isVisible ? t('reviews.external.hideFromPublic') : t('reviews.external.showOnPublic')}
                      >
                        {isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        <span>{isVisible ? t('reviews.external.visible') : t('reviews.external.hidden')}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderWidget = () => (
    <div className="space-y-6">
      {/* Widget preview */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('reviews.widget.title')}</h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Widget configuration */}
          <div className="space-y-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-4">{t('reviews.widget.configuration')}</h4>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('reviews.widget.type')}</label>
                  <select className="w-full p-2 border border-gray-300 rounded-lg">
                    <option>{t('reviews.widget.typeCarousel')}</option>
                    <option>{t('reviews.widget.typeGrid')}</option>
                    <option>{t('reviews.widget.typeList')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('reviews.widget.numberOfReviews')}</label>
                  <div className="flex items-center space-x-4">
                    <select className="flex-1 p-2 border border-gray-300 rounded-lg">
                      <option>3</option>
                      <option>5</option>
                      <option>10</option>
                    </select>
                    <button
                      onClick={() => setShowReviewSelectionModal(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      {t('reviews.widget.selectReviews', { count: selectedReviewsForWidget.length })}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('reviews.widget.filterByRating')}</label>
                  <select className="w-full p-2 border border-gray-300 rounded-lg">
                    <option>{t('reviews.widget.allRatings')}</option>
                    <option>{t('reviews.widget.fiveStarsOnly')}</option>
                    <option>{t('reviews.widget.fourPlusStars')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('reviews.widget.showCompanyResponse')}</label>
                  <select className="w-full p-2 border border-gray-300 rounded-lg">
                    <option>{t('reviews.widget.yes')}</option>
                    <option>{t('reviews.widget.no')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('reviews.widget.ctaButton')}</label>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder={t('reviews.widget.buttonTextPlaceholder')}
                      defaultValue={t('reviews.widget.defaultButtonText')}
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="url"
                      placeholder={t('reviews.widget.buttonUrlPlaceholder')}
                      defaultValue="https://calendly.com/avicenna/demo"
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Embed code */}
            <div>
              <h4 className="font-medium text-gray-900 mb-4">{t('reviews.widget.embedCode')}</h4>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm">
                <code>{`<script src="https://widget.clareefai.com/reviews.js"></script>
<div id="clareefai-reviews" 
     data-company="avicenna"
     data-type="carousel"
     data-count="5"
     data-rating="4"
     data-cta="true">
</div>`}</code>
              </div>
              <button className="mt-2 flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <Eye className="w-4 h-4" />
                <span>{t('reviews.widget.copyCode')}</span>
              </button>
            </div>
          </div>

          {/* Widget preview */}
          <div className="space-y-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-4">{t('reviews.widget.preview')}</h4>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50">
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <div className="text-center mb-4">
                    <h5 className="font-semibold text-gray-900">{t('reviews.widget.previewHeader')}</h5>
                    <div className="flex items-center justify-center space-x-1 mt-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                      <span className="text-sm text-gray-600 ml-2">{t('reviews.widget.previewSummary')}</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {getSelectedReviews().slice(0, 3).map((review) => (
                      <div key={review.id} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                            {review.promoterName?.split(' ').map((n: string) => n[0]).join('') || '?'}
                          </div>
                          <div>
                            <h6 className="font-medium text-gray-900 text-sm">{review.promoterName}</h6>
                            <p className="text-xs text-gray-600">{review.promoterCompany || ''}</p>
                          </div>
                          <div className="flex ml-auto">
                            {[...Array(review.rating || 0)].map((_, i) => (
                              <Star key={i} className="w-3 h-3 text-yellow-400 fill-current" />
                            ))}
                          </div>
                        </div>
                        <p className="text-xs text-gray-700 line-clamp-2">{review.content || ''}</p>
                      </div>
                    ))}
                  </div>

                  <div className="text-center mt-4">
                    <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto">
                      <Phone className="w-4 h-4" />
                      <span>{t('reviews.widget.requestCall')}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Widget analytics */}
            <div>
              <h4 className="font-medium text-gray-900 mb-4">{t('reviews.widget.analytics')}</h4>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">1,247</div>
                  <div className="text-sm text-blue-700">{t('reviews.widget.widgetViews')}</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">89</div>
                  <div className="text-sm text-green-700">{t('reviews.widget.ctaClicks')}</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">23</div>
                  <div className="text-sm text-purple-700">{t('reviews.widget.callsRequested')}</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">7.1%</div>
                  <div className="text-sm text-orange-700">{t('reviews.widget.conversionRate')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageTitle title={t('reviews.pageTitle')} />
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('reviews.heading')}</h1>
          <p className="text-gray-600 mt-1">{t('reviews.subtitle')}</p>
        </div>
        {activeTab === 'reviews' && renderInlineStats()}
      </div>

      {/* Navigation tabs */}
      <nav className="flex space-x-1 bg-gray-200 rounded-xl p-1 mb-6">
        <button
          onClick={() => setActiveTab('reviews')}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${activeTab === 'reviews'
            ? 'bg-purple-600 text-white shadow-md'
            : 'text-gray-600 hover:text-purple-700 hover:bg-white hover:bg-opacity-50'
            }`}
        >
          <Star className="w-4 h-4" />
          <span>{t('reviews.tabs.reviews', { count: allReviews.length })}</span>
        </button>

        <button
          disabled
          type="button"
          className="flex items-center space-x-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 opacity-60 cursor-not-allowed pointer-events-none text-gray-500 bg-gray-100"
        >
          <Globe className="w-4 h-4" />
          <span>{t('reviews.tabs.external')}</span>
        </button>

        <button
          disabled
          type="button"
          className="flex items-center space-x-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 opacity-60 cursor-not-allowed pointer-events-none text-gray-500 bg-gray-100"
        >
          <MessageSquare className="w-4 h-4" />
          <span>{t('reviews.tabs.widget')}</span>
        </button>
      </nav>

      <div>
        {activeTab === 'reviews' && renderReviews()}
        {activeTab === 'external' && renderExternal()}
        {activeTab === 'widget' && renderWidget()}
      </div>

      {/* Review Selection Modal */}
      {showReviewSelectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2001]">
          <div className="bg-white rounded-xl p-6 max-w-6xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">{t('reviews.selectModal.title')}</h3>
              <button
                onClick={() => setShowReviewSelectionModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-600">
                {t('reviews.selectModal.instructions')}
                {selectedReviewsForWidget.length > 0 && (
                  <span className="font-medium text-blue-600 ml-1">
                    {t('reviews.selectModal.selectedCount', { count: selectedReviewsForWidget.length })}
                  </span>
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {allReviews.map((review) => (
                <div
                  key={review.id}
                  onClick={() => handleReviewToggleForWidget(review.id)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${selectedReviewsForWidget.includes(review.id)
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {review.promoterName?.split(' ').map((n: string) => n[0]).join('') || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">{review.promoterName}</h4>
                      <p className="text-sm text-gray-600 truncate">{review.promoterCompany || t('reviews.list.noCompany')}</p>
                      <div className="flex items-center space-x-1 mt-1">
                        {[...Array(review.rating || 0)].map((_, i) => (
                          <Star key={i} className="w-3 h-3 text-yellow-400 fill-current" />
                        ))}
                      </div>
                    </div>
                    {selectedReviewsForWidget.includes(review.id) && (
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    )}
                  </div>

                  <p className="text-xs text-gray-700 line-clamp-3 mb-3">"{review.content || t('reviews.list.noContent')}"</p>

                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{review.productId || ''}</span>
                    <div className="flex items-center space-x-1">
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center pt-4 border-t">
              <div className="text-sm text-gray-600">
                {selectedReviewsForWidget.length === 1
                  ? t('reviews.selectModal.selectedSingular', { count: selectedReviewsForWidget.length })
                  : t('reviews.selectModal.selectedPlural', { count: selectedReviewsForWidget.length })}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowReviewSelectionModal(false)}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => setShowReviewSelectionModal(false)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('reviews.selectModal.apply')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review Details Modal */}
      {selectedReview && (
        <ReviewDetailsModal
          review={selectedReview}
          isOpen={showReviewDetails}
          onClose={() => setShowReviewDetails(false)}
          onUpdateReview={handleUpdateReview}
        />
      )}
    </div>
  );
};

export default ReviewsManagement;